import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { subscribeDataChanged, urlRelevantToChange } from "../lib/dataEvents";

const CACHE_PREFIX = "swr:";

function _userId() {
  try {
    const t = localStorage.getItem("cmsedu_token");
    if (!t) return "";
    const p = JSON.parse(atob(t.split(".")[1]));
    return p.user_id || p.email || p.sub || "";
  } catch { return ""; }
}

function _ck(url) { return url + "::" + _userId(); }

function cacheKeyFor(url) { return CACHE_PREFIX + _ck(url); }

function getCached(url) {
  try {
    const raw = sessionStorage.getItem(cacheKeyFor(url));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.ts > entry.ttl * 1000) {
      sessionStorage.removeItem(cacheKeyFor(url));
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setCached(url, data, ttl = 30) {
  try {
    sessionStorage.setItem(
      cacheKeyFor(url),
      JSON.stringify({ data, ts: Date.now(), ttl })
    );
  } catch {}
}

function invalidateCached(url) {
  try { sessionStorage.removeItem(cacheKeyFor(url)); } catch {}
}

export default function useCachedFetch(url, ttl = 30, deps = []) {
  const [data, setData] = useState(() => getCached(url));
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState(null);
  const [bump, setBump] = useState(0);
  const mountedRef = useRef(true);
  const hasDataRef = useRef(data !== null);
  const depsKey = JSON.stringify(deps);

  useEffect(() => { hasDataRef.current = data !== null; }, [data]);

  useEffect(() => {
    mountedRef.current = true;
    const cached = getCached(url);
    if (cached !== null) {
      setData(cached);
      setLoading(false);
    } else if (!hasDataRef.current) {
      setLoading(true);
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(url);
        if (!mountedRef.current || cancelled) return;
        setData(res.data);
        setCached(url, res.data, ttl);
        setError(null);
      } catch (e) {
        if (!mountedRef.current || cancelled) return;
        setError(e);
      } finally {
        if (mountedRef.current && !cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; mountedRef.current = false; };
  }, [url, ttl, depsKey, bump]);

  // Refresh automatically when the underlying data changes (same tab or cross-tab).
  useEffect(() => {
    const onChanged = (ev) => {
      if (!urlRelevantToChange(url, ev.kind, ev.basePath)) return;
      invalidateCached(url);
      setBump((b) => b + 1);
    };
    return subscribeDataChanged(onChanged);
  }, [url, depsKey]);

  return { data, loading, error };
}
